import { isStrayCypherExpr } from './stray'
import { faultSymptomsCallBlock } from './fault-symptoms'

/**
 * Detail bundle for a single `:Area` (`$areaId` param).
 *
 * Returns:
 *   - `a`                  — the area node itself
 *   - `site`               — the owning `:Site`, resolved by
 *                            following `PART_OF*1..` paths whose
 *                            terminal node is a `:Site`
 *   - `parents[]`          — the ancestor chain (root-first), built
 *                            from the `Area -> Area` portion of the
 *                            longest `PART_OF*` path (ending at a
 *                            `:Site` if one exists, otherwise the
 *                            longest area-only chain). The chain is
 *                            ordered top-down so the breadcrumb
 *                            renders ancestor → ... → direct parent
 *                            without further reversal client-side.
 *   - `subAreaSummaries[]` — direct `<-[:PART_OF]-(:Area)` children,
 *                            each with the same counts the Areas
 *                            landing query returns (subAreaCount,
 *                            equipmentCount, processCount, partCount)
 *                            walking each child's full `PART_OF*0..`
 *                            subtree
 *   - `equipment[]`        — `:Equipment` directly `LOCATED_IN`
 *                            this area (sub-areas' equipment is
 *                            covered by their counts above, not
 *                            duplicated here)
 *   - `processes[]`        — same shape for `:Process`
 *   - `parts[]`            — same shape for `:Part`
 *
 * Bounded by `LIMIT 10000`.
 */
export const PLANT_AREA_DETAIL_CYPHER = `
            MATCH (a:Area {id: $areaId})

            // Ancestor chain — root-first. \`PART_OF*1..\` may target
            // an Area or a Site; we collect both and split below.
            OPTIONAL MATCH path = (a)-[:PART_OF*1..]->(anchor)
              WHERE (anchor:Area OR anchor:Site)
            WITH a, collect(path) AS paths

            // Owning site is whichever path ends in a :Site, if any.
            WITH a, paths,
                 head([p IN paths WHERE last(nodes(p)):Site | last(nodes(p))]) AS site

            // Ancestor areas: the area->area portion of the longest
            // path (which is the one ending in :Site when present,
            // else the longest area-only chain). Top-of-list is the
            // ancestor closest to the site.
            WITH a, site, [
              p IN paths
              WHERE NONE(n IN tail(nodes(p))[..-1] WHERE n:Site)
            ] AS areaPaths
            WITH a, site, areaPaths,
                 head([
                   p IN areaPaths
                   WHERE last(nodes(p)):Site OR all(n IN tail(nodes(p)) WHERE n:Area)
                   | p
                 ]) AS bestPath
            WITH a, site,
                 CASE
                   WHEN bestPath IS NULL THEN []
                   ELSE reverse([n IN tail(nodes(bestPath)) WHERE n:Area | n])
                 END AS parents

            OPTIONAL MATCH (a)<-[:PART_OF]-(sub:Area)
            WITH a, site, parents, collect(distinct sub) AS subAreas
            UNWIND (CASE WHEN size(subAreas) = 0 THEN [null] ELSE subAreas END) AS subNode
            OPTIONAL MATCH (subNode)<-[:PART_OF]-(subChild:Area)
            WITH a, site, parents, subAreas, subNode,
                 count(distinct subChild) AS subSubAreaCount
            OPTIONAL MATCH (subDescendant:Area)-[:PART_OF*0..]->(subNode)
            WITH a, site, parents, subAreas, subNode, subSubAreaCount,
                 collect(distinct subDescendant) AS subSubtree
            OPTIONAL MATCH (eqInSub:Equipment)-[:LOCATED_IN]->(scope:Area)
              WHERE scope IN subSubtree
            WITH a, site, parents, subAreas, subNode, subSubAreaCount, subSubtree,
                 count(distinct eqInSub) AS subEquipmentCount
            OPTIONAL MATCH (procInSub:Process)-[:LOCATED_IN]->(scope:Area)
              WHERE scope IN subSubtree
            WITH a, site, parents, subAreas, subNode, subSubAreaCount, subSubtree,
                 subEquipmentCount, count(distinct procInSub) AS subProcessCount
            OPTIONAL MATCH (partInSub:Part)-[:LOCATED_IN]->(scope:Area)
              WHERE scope IN subSubtree
            WITH a, site, parents, subAreas, subNode, subSubAreaCount,
                 subEquipmentCount, subProcessCount,
                 count(distinct partInSub) AS subPartCount
            WITH a, site, parents,
                 collect(distinct {
                   area: subNode,
                   subAreaCount: subSubAreaCount,
                   equipmentCount: subEquipmentCount,
                   processCount: subProcessCount,
                   partCount: subPartCount
                 }) AS subAreaSummaries

            OPTIONAL MATCH (eq:Equipment)-[:LOCATED_IN]->(a)
            WITH a, site, parents, subAreaSummaries,
                 collect(distinct eq) AS equipment
            OPTIONAL MATCH (proc:Process)-[:LOCATED_IN]->(a)
            WITH a, site, parents, subAreaSummaries, equipment,
                 collect(distinct proc) AS processes
            OPTIONAL MATCH (part:Part)-[:LOCATED_IN]->(a)
            WITH a,
                 site,
                 parents,
                 subAreaSummaries,
                 equipment,
                 processes,
                 collect(distinct part) AS parts
            ${faultSymptomsCallBlock('a')}
            RETURN a,
                   site,
                   parents,
                   subAreaSummaries,
                   equipment,
                   processes,
                   parts,
                   faults,
                   ${isStrayCypherExpr('a', 'Area')} AS isStray
            LIMIT 10000
          `
